import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import "./QuestionsNeedingAttention.scss";

/**
 * QuestionsNeedingAttention
 * 
 * A prioritized list of questions that need teacher review.
 * Sorted by score (lowest first) to surface problem areas.
 * 
 * Decision it enables: "Which questions should I revise or review responses for?"
 */
export default function QuestionsNeedingAttention({ 
  insights = [], 
  environmentData = {},
  limit = 5 
}) {
  const navigate = useNavigate();

  const prioritizedQuestions = useMemo(() => {
    if (!insights || insights.length === 0) return [];

    return insights
      .filter(i => (i.responseCount || 0) > 0) // Only show questions with responses
      .map(insight => ({
        insightId: insight.insightId,
        question: insight.textValue || insight.question || "Untitled question",
        score: Math.round(insight.meanScore || 0),
        responseCount: insight.responseCount || 0,
        environmentId: insight.environmentId,
        environmentName: environmentData[insight.environmentId] || null,
      }))
      .sort((a, b) => a.score - b.score) // Lowest scores first (most concerning)
      .slice(0, limit);
  }, [insights, environmentData, limit]);

  const getScoreLevel = (score) => {
    if (score >= 70) return { level: "good", icon: "✓", label: "On Track" };
    if (score >= 40) return { level: "warning", icon: "◐", label: "Review" };
    return { level: "critical", icon: "!", label: "Needs Attention" };
  };

  const handleQuestionClick = (insight) => {
    const insightId = insight.insightId || "";
    const q = insight.question || "";
    navigate(`/question?insightId=${encodeURIComponent(insightId)}&q=${encodeURIComponent(q)}`);
  };

  if (prioritizedQuestions.length === 0) {
    return (
      <div className="questionsAttention">
        <div className="qaEmpty">
          <div className="qaEmptyIcon">📋</div>
          <div className="qaEmptyText">No questions with responses yet</div>
          <div className="qaEmptySub">Questions will appear here once students respond</div>
        </div>
      </div>
    );
  }

  // Calculate summary stats
  const criticalCount = prioritizedQuestions.filter(q => q.score < 40).length;
  const warningCount = prioritizedQuestions.filter(q => q.score >= 40 && q.score < 70).length;

  return (
    <div className="questionsAttention">
      {/* Summary bar */}
      {(criticalCount > 0 || warningCount > 0) && (
        <div className="qaSummary">
          {criticalCount > 0 && (
            <span className="qaSummaryItem critical">
              <span className="qaSummaryIcon">!</span>
              {criticalCount} need{criticalCount === 1 ? 's' : ''} attention
            </span>
          )}
          {warningCount > 0 && (
            <span className="qaSummaryItem warning">
              <span className="qaSummaryIcon">◐</span>
              {warningCount} to review
            </span>
          )}
        </div>
      )}

      {/* Question list */}
      <div className="qaList">
        {prioritizedQuestions.map((item, idx) => {
          const scoreInfo = getScoreLevel(item.score);
          
          return (
            <div 
              key={item.insightId || idx} 
              className={`qaItem qaItem--${scoreInfo.level}`}
              onClick={() => handleQuestionClick(item)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => e.key === "Enter" && handleQuestionClick(item)}
            >
              {/* Score indicator */}
              <div className={`qaScoreIndicator qaScoreIndicator--${scoreInfo.level}`}>
                <span className="qaScoreIcon">{scoreInfo.icon}</span>
              </div>

              {/* Content */}
              <div className="qaContent">
                <div className="qaQuestion">{item.question}</div>
                <div className="qaMeta">
                  <span className="qaMetaItem qaScore">
                    <strong>{item.score}%</strong> avg score
                  </span>
                  <span className="qaMetaDivider">·</span>
                  <span className="qaMetaItem">
                    {item.responseCount} response{item.responseCount !== 1 ? 's' : ''}
                  </span>
                  {item.environmentName && (
                    <>
                      <span className="qaMetaDivider">·</span>
                      <span className="qaMetaItem qaEnv">{item.environmentName}</span>
                    </>
                  )}
                </div>
              </div>

              {/* Action arrow */}
              <div className="qaAction">
                <span className="qaArrow">→</span>
              </div>
            </div>
          );
        })}
      </div>

      {/* Footer hint */}
      <div className="qaFooter">
        Click any question to view student responses
      </div>
    </div>
  );
}
